// Manœuvres : icônes SVG et libellés français, partagés par le bandeau de guidage et la
// modale d'export. Module pur.

// Flèches PLEINES, de style panneau routier : hampe épaisse, pointe massive, une seule forme
// fermée. Un contour au trait reste un contour au trait quand on l'agrandit — c'est ce qui les
// rendait molles dans un autobus en mouvement.
//
// Le conteneur SVG doit donc être en « fill="currentColor" stroke="none" » (voir #nav-arrow
// dans index.template.html) : l'inverse afficherait des silhouettes vides, sans erreur.
//
// Trois formes de base, les autres en dérivent par rotation. Écrire treize tracés à la main
// les ferait diverger de dessin ; les angles ont été choisis pour que la forme tournée reste
// dans le viewBox 24×24.
const ARROW_UP   = '<path d="M12 1.5L19.5 11H15.5V22.5H8.5V11H4.5Z"/>';
const TURN_LEFT  = '<path d="M1 9L7 2V6H15V22.5H9V12H7V16Z"/>';
const TURN_RIGHT = '<path d="M23 9L17 2V6H9V22.5H15V12H17V16Z"/>';   // TURN_LEFT en miroir

const MANEUVER_ICONS = {
    'straight':     ARROW_UP,
    'left':         TURN_LEFT,
    'right':        TURN_RIGHT,
    // Virage doux : la flèche droite simplement inclinée.
    'slight-left':  `<g transform="rotate(-28 12 22)">${ARROW_UP}</g>`,
    'slight-right': `<g transform="rotate(28 12 22)">${ARROW_UP}</g>`,
    // Virage serré : bras vers le côté, puis pointe vers le BAS — on repart en sens inverse,
    // c'est ce qui distingue « serré » de « à gauche ». Faire pivoter TURN_LEFT donnait un
    // zigzag illisible : ces deux formes sont donc tracées directement.
    'sharp-left':   '<path d="M8 9H19.5V22.5H13.5V15L8 23L2.5 15H8V9Z"/>',
    'sharp-right':  '<path d="M16 9H4.5V22.5H10.5V15L16 23L21.5 15H16V9Z"/>',
    // Demi-tour : montée à droite, boucle par le haut, pointe vers le bas à gauche.
    'uturn':        '<path d="M20 22.5V9.5A8 8 0 0 0 4 9.5V14H1L6.5 22.5L12 14H9V9.5A3 3 0 0 1 15 9.5V22.5Z"/>',
    // Rond-point : anneau plein (fill-rule evenodd perce le centre — deux <circle> le
    // rempliraient tous les deux) et une flèche de sortie.
    'roundabout':   '<path fill-rule="evenodd" d="M10.5 6.5A8 8 0 1 1 10.49 6.5ZM10.5 10.5A4 4 0 1 0 10.51 10.5Z"/>'
                  + '<path d="M19 1L23.5 7H20.5V13H17.5V7H14.5Z"/>',
    'depart':       '<circle cx="12" cy="20" r="3.5"/><path d="M12 1.5L18.5 9.5H14.5V16H9.5V9.5H5.5Z"/>',
    'arrive':       '<path d="M4.5 1.5H8V22.5H4.5Z"/><path d="M9.5 2.5H20.5L17.3 7L20.5 11.5H9.5Z"/>',
    // Insertion : une voie qui rejoint l'autre en biais. La barre doit TOUCHER la hampe,
    // sinon elle se lit comme un élément décoratif détaché.
    'merge-left':   '<path d="M9 1.5L15.5 9.5H12V22.5H6V9.5H2.5Z"/><path d="M14.5 22.5H19.5L13 10.5L9.8 12.6Z"/>',
    'merge-right':  '<path d="M15 1.5L8.5 9.5H12V22.5H18V9.5H21.5Z"/><path d="M9.5 22.5H4.5L11 10.5L14.2 12.6Z"/>'
};


const VALHALLA_MANEUVERS = {
    1:  ['depart', 'straight'],   2:  ['depart', 'right'],      3:  ['depart', 'left'],
    4:  ['arrive', 'straight'],   5:  ['arrive', 'right'],      6:  ['arrive', 'left'],
    7:  ['new name', 'straight'], 8:  ['continue', 'straight'],
    9:  ['turn', 'slight right'], 10: ['turn', 'right'],        11: ['turn', 'sharp right'],
    12: ['turn', 'uturn'],        13: ['turn', 'uturn'],
    14: ['turn', 'sharp left'],   15: ['turn', 'left'],         16: ['turn', 'slight left'],
    17: ['on ramp', 'straight'],  18: ['on ramp', 'right'],     19: ['on ramp', 'left'],
    20: ['off ramp', 'right'],    21: ['off ramp', 'left'],
    22: ['fork', 'straight'],     23: ['fork', 'right'],        24: ['fork', 'left'],
    25: ['merge', 'straight'],    37: ['merge', 'right'],       38: ['merge', 'left'],
    26: ['roundabout', 'right'],  27: ['exit roundabout', 'right']
};


function describeManeuver(step) {
    const m = step.maneuver;
    const t = m.type;
    const mod = m.modifier || '';   // absent sur certains types (merge, ramp) chez OSRM
    const rawName = step.name || '';
    // OSRM et Valhalla laissent souvent le nom de rue vide. On omet alors la
    // subordonnée au lieu de meubler : « la route sans nom » passait à l'écrit,
    // c'est mauvais prononcé à voix haute.
    const on = rawName ? ` sur ${rawName}` : '';
    const vers = rawName ? ` vers ${rawName}` : '';

    let key = 'straight';
    if (mod === 'left') key = 'left';
    else if (mod === 'right') key = 'right';
    else if (mod === 'slight left') key = 'slight-left';
    else if (mod === 'slight right') key = 'slight-right';
    else if (mod === 'sharp left') key = 'sharp-left';
    else if (mod === 'sharp right') key = 'sharp-right';
    else if (mod === 'uturn') key = 'uturn';

    if (t === 'merge') key = mod.indexOf('left') !== -1 ? 'merge-left'
                          : mod.indexOf('right') !== -1 ? 'merge-right' : 'straight';
    if (t === 'roundabout' || t === 'rotary' || t === 'exit roundabout' || t === 'exit rotary') key = 'roundabout';
    if (t === 'depart') key = 'depart';
    if (t === 'arrive') key = 'arrive';

    let text;
    switch (t) {
        case 'depart': text = `Démarrer${on}`; break;
        case 'arrive': text = `Arrivée à l'arrêt`; break;
        case 'roundabout': case 'rotary':
            text = m.exit ? `Au rond-point, prendre la ${m.exit === 1 ? '1re' : m.exit + 'e'} sortie${vers}`
                          : `Au rond-point, prendre la sortie${vers}`;
            break;
        case 'exit roundabout': case 'exit rotary':
            text = `Sortir du rond-point${on}`; break;
        case 'end of road':
            text = mod.indexOf('left') !== -1 ? `Au bout de la rue, tourner à gauche${on}`
                 : mod.indexOf('right') !== -1 ? `Au bout de la rue, tourner à droite${on}`
                 : `Au bout de la rue, continuer${on}`;
            break;
        case 'fork':
            text = mod.indexOf('left') !== -1 ? `À l'embranchement, garder la gauche${on}`
                 : mod.indexOf('right') !== -1 ? `À l'embranchement, garder la droite${on}`
                 : `À l'embranchement, continuer${on}`;
            break;
        case 'merge':   text = rawName ? `Rejoindre ${rawName}` : `Rejoindre la voie`; break;
        case 'on ramp': text = `Prendre la bretelle d'accès${vers}`; break;
        case 'off ramp': text = `Prendre la sortie${vers}`; break;
        default: {
            let act = "Continuer";
            if (mod === 'uturn') act = "Faire demi-tour";
            else if (mod === 'slight left') act = "Prendre légèrement à gauche";
            else if (mod === 'sharp left') act = "Prendre un virage serré à gauche";
            else if (mod === 'slight right') act = "Prendre légèrement à droite";
            else if (mod === 'sharp right') act = "Prendre un virage serré à droite";
            else if (mod === 'left') act = "Tourner à gauche";
            else if (mod === 'right') act = "Tourner à droite";
            text = `${act}${on}`;
        }
    }

    // Libellé court du bandeau : le nom de rue y est déjà affiché sur sa propre ligne.
    const ACTIONS = {
        'left': 'Tourner à gauche', 'right': 'Tourner à droite',
        'slight-left': 'Légèrement à gauche', 'slight-right': 'Légèrement à droite',
        'sharp-left': 'Virage serré à gauche', 'sharp-right': 'Virage serré à droite',
        'uturn': 'Faire demi-tour', 'roundabout': 'Rond-point',
        'straight': 'Continuer tout droit', 'depart': 'Départ', 'arrive': 'Arrivée',
        'merge-left': 'Rejoindre à gauche', 'merge-right': 'Rejoindre à droite'
    };
    // name = brut (vide si OSRM n'a pas de nom de rue) : le bandeau affiche alors
    // l'action plutôt que le remplissage « la route sans nom » en text-5xl.
    return { key: key, name: rawName, text: text, action: ACTIONS[key] || 'Continuer' };
}


function translateInstruction(step) {
    return describeManeuver(step).text;
}


export { MANEUVER_ICONS, VALHALLA_MANEUVERS, describeManeuver, translateInstruction };
